import { gsap } from 'gsap';
import { TextPlugin } from 'gsap/TextPlugin';
import Swup from 'swup';
import SwupHeadPlugin from '@swup/head-plugin';
import SwupProgressPlugin from '@swup/progress-plugin';


function isFrontPage(url) {
  try {
    const target = new URL(url, window.location.origin);
    const front = new URL(window.oxgData.front_page_url, window.location.origin);
    return (
      target.origin === front.origin &&
      target.pathname.replace(/\/$/, '') === front.pathname.replace(/\/$/, '')
    );
  } catch (e) {
    return false;
  }
}


const swup = new Swup({
  containers: ['#brx-content', '#brx-header', '#brx-footer'],
  ignoreVisit: (url, { el }) => {
    const hasAllContainers = ['#brx-content', '#brx-header', '#brx-footer']
      .every((selector) => document.querySelector(selector));
    if (!hasAllContainers) return true;

    if (isFrontPage(url)) return true;

    return false;
  },
  plugins: [
    new SwupHeadPlugin({ persistAssets: true }),
    new SwupProgressPlugin({
      className: 'oxg-progress-bar', // eigener Klassenname statt swup-progress-bar
      delay: 100,                    // Balken erscheint erst ab 300ms Ladezeit
      finishAnimation: true,         // läuft bei Abschluss sauber bis 100%
    }),
  ],
});

gsap.registerPlugin(TextPlugin);

swup.hooks.before('link:click', (visit) => {
  const targetUrl = new URL(visit.to.url, window.location.origin);
  const currentParams = new URLSearchParams(window.location.search);

  for (const [key, value] of currentParams.entries()) {
    if (!targetUrl.searchParams.has(key)) {
      targetUrl.searchParams.set(key, value);
    }
  }

  if (!targetUrl.searchParams.has('country')) {
    targetUrl.searchParams.set('country', getOrSetCountry());
  }

  visit.to.url = targetUrl.pathname + targetUrl.search;
});

//Get or set country
function getOrSetCountry(defaultCountry = 'de') {
  const url = new URL(window.location.href);
  let country = url.searchParams.get('country');
  if (!country) {
    country = defaultCountry;
    url.searchParams.set('country', country);
    window.history.replaceState({}, '', url);
  }
  return country;
}

function updatePageNav() {
  const currentPageNo = document.querySelector('.current-page-number');
  const totalPagesNo = document.querySelector('.total-pages-number');
  if (window.oxgData.index === null || window.oxgData.index === undefined) return;

  if (currentPageNo) {
    gsap.killTweensOf(currentPageNo);
    currentPageNo.textContent = String(window.oxgData.index);
  }
  if (totalPagesNo && window.oxgData.num_pages != null) {
    totalPagesNo.textContent = String(window.oxgData.num_pages);
  }
}

let pendingDataFetch = null;

function updateLocalizedData() {
  const newUrl = window.location.href;

  if (pendingDataFetch) pendingDataFetch.abort();
  const controller = new AbortController();
  pendingDataFetch = controller;

  return fetch(`${window.oxgData.ajax_url}?action=oxg_get_page_data&url=${encodeURIComponent(newUrl)}`, {
    signal: controller.signal,
  })
    .then(r => r.json())
    .then(data => {
      if (pendingDataFetch !== controller) return; // durch neueren Request überholt
      if (data.success) {
        const pageData = data.data;
        window.oxgData.page_id      = pageData.page_id;
        window.oxgData.page_title   = pageData.page_title;
        window.oxgData.is_frontpage = pageData.is_frontpage;
        window.oxgData.regions      = pageData.regions;
        window.oxgData.in_menu      = pageData.in_menu;
        window.oxgData.url          = pageData.url;
        window.oxgData.prev_url     = pageData.prev_url;
        window.oxgData.next_url     = pageData.next_url;
        window.oxgData.num_pages    = pageData.num_pages;
        window.oxgData.index        = pageData.index      !== null ? pageData.index + 1      : null;
        window.oxgData.prev_index   = pageData.prev_index !== null ? pageData.prev_index + 1 : null;
        window.oxgData.next_index   = pageData.next_index !== null ? pageData.next_index + 1 : null;

        // Erst JETZT ist window.oxgData frisch -> jetzt ins DOM schreiben.
        updatePageNav();
      }
    })
    .catch(err => {
      if (err.name !== 'AbortError') console.error(err);
    });
}

function syncProgressBarPosition() {
  const bar = document.querySelector('.oxg-progress-bar');
  if (!bar) return;

  const content = document.getElementById('brx-content');
  if (content) {
    const rect = content.getBoundingClientRect();
    bar.style.top = `${rect.top}px`;
    bar.style.left = `${rect.left}px`;
    bar.style.width = `${rect.width}px`;
  } else {
    bar.style.top = '';
    bar.style.left = '';
    bar.style.width = '';
  }
}

window.addEventListener('resize', syncProgressBarPosition);
window.addEventListener('orientationchange', syncProgressBarPosition);
window.addEventListener('appFrameSet', syncProgressBarPosition);
window.addEventListener('appFrameRemoved', syncProgressBarPosition);

function reapplyAppFrame() {
  const frame = document.getElementById('oxg-app-frame');
  if (!frame) return; // Mobile/kein gerahmter Modus aktiv – nichts zu tun

  const header = document.getElementById('brx-header');
  const content = document.getElementById('brx-content');
  const footer = document.getElementById('brx-footer');


  if (header && header.parentNode !== frame) frame.appendChild(header);
  if (content && content.parentNode !== frame) frame.appendChild(content);
  if (footer && footer.parentNode !== frame) frame.appendChild(footer);

  if (header) header.classList.add('is-framed');
  if (content) content.classList.add('is-framed');
  if (footer) footer.classList.add('is-framed');
}

let lastActiveTopLevelHref = (() => {
  const topLevelLi = document.querySelector('.oxg-main-menu-list.menu-level-0 > li.current-menu-ancestor');
  return topLevelLi ? topLevelLi.querySelector(':scope > a')?.getAttribute('href') : null;
})();


function animateSubMenus() {
  const topLevelLi = document.querySelector('.oxg-main-menu-list.menu-level-0 > li.current-menu-ancestor');
  const currentHref = topLevelLi ? topLevelLi.querySelector(':scope > a')?.getAttribute('href') : null;
  const topLevelSubMenus = document.querySelectorAll('.sub-menu.menu-level-1');

  if (currentHref && currentHref === lastActiveTopLevelHref) {
    // Gleicher Zweig war schon vorher offen -> direkt sichtbar setzen, nicht neu animieren
    topLevelSubMenus.forEach((el) => {
      gsap.set(el, { height: 'auto', opacity: 1, overflow: '' });
    });
  } else {
    // Zweig hat gewechselt (oder war vorher zu) -> Aufklapp-Animation
    topLevelSubMenus.forEach((el) => {
      gsap.fromTo(
        el,
        { height: 0, opacity: 0 },
        {
          height: 'auto',
          opacity: 1,
          duration: 0.4,
          ease: 'power1.out',
          onStart: () => { el.style.overflow = 'hidden'; },
          onComplete: () => { el.style.overflow = ''; },
        }
      );
    });
  }

  lastActiveTopLevelHref = currentHref;
}
// Vorher: cleanup lief hier
swup.hooks.on('visit:start', () => {
  killLingeringTweens();
  window.oxgCleanup.runAll();
  // Plugin fügt den Balken erst nach seiner eigenen "delay"-Option (100ms) ein,
  // daher hier etwas später synchronisieren statt sofort.
  setTimeout(syncProgressBarPosition, 150);
});

swup.hooks.on('content:replace', () => {
  killLingeringTweens();
  window.oxgCleanup.runAll();
  updateLocalizedData();
  getOrSetCountry();
  reapplyAppFrame();
  animateSubMenus();
  document.dispatchEvent(new CustomEvent('oxg:contentReplaced'));
});

//Page Transition Out
swup.hooks.replace('animation:out:await', async () => {
  if (!document.querySelector('#brx-content')) return;
  await gsap.fromTo('#brx-content', { opacity: 1 }, { opacity: 0, y: 0, duration: 0.3 });
});

//Page Transition In
swup.hooks.replace('animation:in:await', async () => {
  if (!document.querySelector('#brx-content')) return;
  await gsap.fromTo('#brx-content', { opacity: 0 , y: 0 }, { opacity: 1 , y: 0, duration: 0.3 });
});


window.oxgCleanup = window.oxgCleanup || {
  _tasks: [],
  register(fn) {
    if (typeof fn === 'function') this._tasks.push(fn);
  },
  runAll() {
    while (this._tasks.length) {
      const fn = this._tasks.pop();
      try { fn(); } catch (err) { console.warn('oxgCleanup task failed:', err); }
    }
  },
};

function killLingeringTweens() {
  ['#brx-content', '#brx-header', '#brx-footer', '.current-page-number'].forEach(selector => {
    document.querySelectorAll(selector).forEach(el => gsap.killTweensOf(el));
  });
}

document.addEventListener('oxg:contentReplaced', () => {
  setTimeout(set_studio_active, 100);
});